import { Component, Inject, OnInit, ChangeDetectorRef, ViewChildren, QueryList } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatExpansionPanel } from '@angular/material/expansion';
import { LEAD_BASED_PAINT } from '@shared/utils/app-static-data';
import { SharedService } from 'app/services/core/shared.service';
import { AddEditIndividualComponent } from 'app/components/individual/add-edit-individual/add-edit-individual.component';
import { MergeIndividualComponent } from 'app/components/individual/merge-individual/merge-individual.component';
import { IPerson } from 'app/models/People/person';
import { IPossiblePersonMatchFilter } from 'app/models/filters/possiblePersonMatchFilter';
import { PersonService } from 'app/services/person/person.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-match-student-uploads-to-existing',
  templateUrl: './match-student-uploads-to-existing.component.html',
  styleUrls: ['./match-student-uploads-to-existing.component.scss']
})
export class MatchStudentUploadsToExistingComponent implements OnInit {
  public uiData = LEAD_BASED_PAINT;
  public isMobile: boolean = false;
  public loading: boolean = false;
  public columnNames: string[] = ['id', 'firstName', 'lastName', 'middleName', 'dateOfBirth', 'response', 'actions'];
  public columnData: any = {    
    id: { displayName: 'ID', displaySize: 8, displayType: 'number' },
    firstName: { displayName: 'First Name', displaySize: 15, displayType: 'string' },
    lastName: { displayName: 'Last Name', displaySize: 15, displayType: 'string' },
    middleName: { displayName: 'Middle Name', displaySize: 10, displayType: 'string' },
    dateOfBirth: { displayName: 'Date of Birth', displaySize: 15, displayType: 'date' },
    response: { displayName: 'Address', displaySize: 20, displayType: 'string' },
    actions: { displayName: 'Actions', displaySize: 12, displayType: 'button' }
  };
  public dataSource: MatTableDataSource<any>[] = [];  

  @ViewChildren(MatExpansionPanel) panels: QueryList<MatExpansionPanel>;

  constructor(
    private readonly personService:PersonService,
    private readonly toastr: ToastrService,
    public sharedService: SharedService,
    public dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<MatchStudentUploadsToExistingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {    
    this.data.students.sort((a: any, b: any) => {
      const keyA = `${a.firstName}${a.lastName}`;
      const keyB = `${b.firstName}${b.lastName}`;
      const matchesA = this.data.possibleMatches[keyA] || [];
      const matchesB = this.data.possibleMatches[keyB] || [];
      return matchesA.length === 0 ? 1 : matchesB.length === 0 ? -1 : 0;
    });

    this.data.students.forEach((student: any) => {
      const key = `${student.firstName}${student.lastName}`;
      const matches = this.data.possibleMatches[key] || [];

      const fullStudentAddress = this.normalize(student.streetAddress) + this.normalize(student.city) + ' ,' + this.normalize(student.state) + this.normalize(student.zipCode);

      // Classify matches into three buckets
      const bothMatches: any[] = [];
      const dobOnlyMatches: any[] = [];
      const addressOnlyMatches: any[] = [];

      matches.forEach((match: any) => {
        const response = this.normalize(match.response);
        const dobMatch = student.dateOfBirth && match.dateOfBirth === student.dateOfBirth;
        const addressMatch = response.includes(fullStudentAddress);

        if (dobMatch && addressMatch) {
          bothMatches.push(match);
        } else if (dobMatch) {
          dobOnlyMatches.push(match);
        } else if (addressMatch) {
          addressOnlyMatches.push(match);
        }
      });

      let selectedMatch = null;

      if (bothMatches.length > 0) {
        selectedMatch = bothMatches.reduce((prev, curr) => curr.id < prev.id ? curr : prev);
      } else if (dobOnlyMatches.length > 0) {
        selectedMatch = dobOnlyMatches.reduce((prev, curr) => curr.id < prev.id ? curr : prev);
      } else if (addressOnlyMatches.length > 0) {
        selectedMatch = addressOnlyMatches.reduce((prev, curr) => curr.id < prev.id ? curr : prev);
      }

      if (selectedMatch) {
        student.selectedPerson = selectedMatch;
      }
      
      student.matchCount = bothMatches.length + dobOnlyMatches.length + addressOnlyMatches.length;
      student.selectedIndividuals = [];  // Initialize selected array for each student

      this.dataSource.push(new MatTableDataSource(matches));
    });
    this.cdr.detectChanges();
  }

  normalize(str): string {
    if (typeof str === 'string') {
      return str.trim().toLowerCase();
    }
    return '';
  }

  getBackgroundColor(student: any): string {
    if (student.selectedPerson) {
      return student.matchCount > 1 ? 'rgba(255, 255, 0, 0.3)' : 'rgba(0, 255, 0, 0.3)';
    }    
  }

  openNextPanel(index: number): void {
    const panelsArray = this.panels.toArray();
  
    for (let i = index; i < panelsArray.length; i++) {
      const currentPanel = panelsArray[i];
      const student = this.data.students[i];
  
      if ((!student.selectedPerson && !currentPanel.disabled) && this.dataSource[i].data.length > 0) {
        currentPanel.open();
        break;
      }
    }
  }
  
  updateSelection(row: any, event: any, index: number): void {      
    const student = this.data.students[index];      
    student.selectedPerson = row?.id ? row : null;
    this.cdr.detectChanges();
    const currentPanel = this.panels.toArray()[index];
    if (currentPanel) {
      currentPanel.close();
    }

    this.openNextPanel(index + 1);    
  }
  
  clearSelection(index: number, event: any): void {
    event.stopPropagation();
    const student = this.data.students[index];
    student.selectedPerson = null;
    this.cdr.detectChanges();    
  }

  toggleSelection(person: IPerson, index: number): void {    
    const student = this.data.students[index];
    
    if (!student.selectedIndividuals) {
      student.selectedIndividuals = [];
    }
    
    const personIndex = student.selectedIndividuals.findIndex(p => p.id === person.id);
    
    if (personIndex > -1) {
      // Remove from array if already selected
      student.selectedIndividuals.splice(personIndex, 1);
    } else {
      // Add to array if not selected
      student.selectedIndividuals.push(person);
    }
    
    this.cdr.detectChanges();
  }

  isSelected(person: IPerson, index: number): boolean {
    const student = this.data.students[index];
    if (!student.selectedIndividuals) {
      return false;
    }
    return student.selectedIndividuals.some(p => p.id === person.id);
  }

  returnResults() {
    this.dialogRef.close(this.data);
  }

  editPerson(person: IPerson, event: any) {
    event.stopPropagation();    
    const dialogRef = this.dialog.open(AddEditIndividualComponent, {
      width: '90%',
      data: {person: person},});
    dialogRef.afterClosed().subscribe(result => {      
    });
  }

  mergeIndividuals(selectedIndividuals: IPerson[], studentIndex: number, event?: any) {
    if (event) {
      event.stopPropagation();
    }
    
    const dialogRef = this.dialog.open(MergeIndividualComponent, {
      width: '90%',
      autoFocus: false,
      data: selectedIndividuals,});
    dialogRef.afterClosed().subscribe(result => {  
      if(result){
        const student = this.data.students[studentIndex];
        
        // Clear the selectedIndividuals array
        student.selectedIndividuals = [];
        
        // Refresh possible matches using getStudentMatches
        this.getStudentMatches(student, studentIndex);
      }
    });
  }

  getMergeTooltip(selectedIndividuals: IPerson[]): string {
    if (selectedIndividuals != null && selectedIndividuals.length > 0) {
        return 'Merge: ' + selectedIndividuals.map(individual => `${individual.id}: ${individual.firstName} ${individual.lastName}`).join(', ');
    }
    return 'Merges selected individuals';
  }

  getStudentMatches(person: any, studentIndex: number){
    let filter:IPossiblePersonMatchFilter[] = [];
    
    let possibleMatch:IPossiblePersonMatchFilter = {
      firstName: person.firstName,
      lastName: person.lastName,
      phone: null,
      email: null,
      isComplaintSource: false,
      address: (person.streetAddress + ',' || ' ,') + (person.city ? person.city + ',' : ' ,') + (person.state ? person.state + ',' : ' ,') + (person.zipCode ? person.zipCode : ' ')
    }
    filter.push(possibleMatch);

    this.personService.getMatchingPeople(filter).subscribe(result=>{
      const key = `${person.firstName}${person.lastName}`;
      const matches = result || [];
      
      // Update the possibleMatches in data
      this.data.possibleMatches[key] = matches;
      
      // Update the dataSource for this student
      this.dataSource[studentIndex].data = matches;
      
      // Recalculate matchCount with the same logic from ngOnInit
      const fullStudentAddress = this.normalize(person.streetAddress) + this.normalize(person.city) + ' ,' + this.normalize(person.state) + this.normalize(person.zipCode);
      
      const bothMatches: any[] = [];
      const dobOnlyMatches: any[] = [];
      const addressOnlyMatches: any[] = [];
      
      matches.forEach((match: any) => {
        const response = this.normalize(match.response);
        const dobMatch = person.dateOfBirth && match.dateOfBirth === person.dateOfBirth;
        const addressMatch = response.includes(fullStudentAddress);

        if (dobMatch && addressMatch) {
          bothMatches.push(match);
        } else if (dobMatch) {
          dobOnlyMatches.push(match);
        } else if (addressMatch) {
          addressOnlyMatches.push(match);
        }
      });
      
      person.matchCount = bothMatches.length + dobOnlyMatches.length + addressOnlyMatches.length;
      
      // Check if selectedPerson still exists in refreshed matches
      if (person.selectedPerson) {
        const matchedPerson = matches.find((match: any) => match.id === person.selectedPerson.id);
        
        if (matchedPerson) {
          // Update the reference to the refreshed object so radio button binding works
          person.selectedPerson = matchedPerson;
        } else {
          person.selectedPerson = null;
        }
      }
      
      this.cdr.detectChanges();
    },error=>{
      this.toastr.error("Failed to get student matches:", error);
    });
  }
}